import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Copy, Pencil, Trash2 } from "lucide-react";
import { type ReactNode, useMemo, useState } from "react";
import {
  Badge,
  Button,
  buttonVariants,
  Card,
  Dialog,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  useToast,
} from "@rumbledirect/ui-v2";
import { BuildAccessoriesTab } from "@/configurator/vehicleConversions/BuildAccessoriesTab";
import { ConversionBuildsTab } from "@/configurator/vehicleConversions/ConversionBuildsTab";
import { ConversionExclusionGroupsTab } from "@/configurator/vehicleConversions/ConversionExclusionGroupsTab";
import { ConversionOptionsTab } from "@/configurator/vehicleConversions/ConversionOptionsTab";
import { ConfiguratorOptionKind } from "@/configurator/vehicleConversions/vehicleConversion";
import { VehicleConversionMediaTab } from "@/configurator/vehicleConversions/VehicleConversionMediaTab";
import { useCopyVehicleConversion } from "@/configurator/vehicleConversions/useCopyVehicleConversion";
import { useDeleteVehicleConversion } from "@/configurator/vehicleConversions/useDeleteVehicleConversion";
import { useVehicleTypeOptions } from "@/configurator/vehicleConversions/useVehicleTypeOptions";
import { RequirePermission } from "@/currentUser/RequirePermission";
import { useGetAdminConfiguratorVehicleConversionsVehicleConversionId } from "@/lib/api/generated/configurator/configurator";

const UPDATE_PERMISSION = "admin_configurator_vehicle_conversion_update";
const COPY_PERMISSION = "admin_configurator_vehicle_conversion_copy";
const DELETE_PERMISSION = "admin_configurator_vehicle_conversion_delete";

const DETAIL_TABS = [
  "algemeen",
  "media",
  "inrichting",
  "accessoires",
  "belettering",
  "telematica",
  "aanvullend",
  "uitsluitingen",
] as const;
type DetailTab = (typeof DETAIL_TABS)[number];
const DEFAULT_TAB: DetailTab = "algemeen";

function isDetailTab(value: unknown): value is DetailTab {
  return DETAIL_TABS.includes(value as DetailTab);
}

interface VehicleConversionShowSearch {
  readonly tab: DetailTab;
}

export const Route = createFileRoute(
  "/_authenticated/configurator/vehicle-conversions_/$vehicleConversionId",
)({
  validateSearch: (
    search: Record<string, unknown>,
  ): VehicleConversionShowSearch => ({
    tab: isDetailTab(search.tab) ? search.tab : DEFAULT_TAB,
  }),
  component: VehicleConversionShowPage,
});

function VehicleConversionShowPage(): ReactNode {
  const { vehicleConversionId } = Route.useParams();
  const { tab } = Route.useSearch();
  const navigate = useNavigate();
  const routeNavigate = Route.useNavigate();
  const toast = useToast();

  const { data, isPending, isError, refetch } =
    useGetAdminConfiguratorVehicleConversionsVehicleConversionId(
      vehicleConversionId,
    );

  const { options: vehicleTypeOptions } = useVehicleTypeOptions();
  const vehicleTypeLabel = useMemo(() => {
    if (!data?.vehicleType) return "—";
    return (
      vehicleTypeOptions.find((option) => option.value === data.vehicleType)
        ?.label ?? data.vehicleType
    );
  }, [vehicleTypeOptions, data?.vehicleType]);

  const copyMutation = useCopyVehicleConversion();
  const deleteMutation = useDeleteVehicleConversion();
  const [confirmDelete, setConfirmDelete] = useState(false);

  const title = data?.title || "Voertuigconversie";

  return (
    <div className="space-y-5">
      <Link
        to="/configurator/vehicle-conversions"
        search={{ page: 1 }}
        className="inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="size-4" />
        Terug naar voertuigconversies
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-4">
        {isPending ? (
          <Skeleton className="h-8 w-64" />
        ) : (
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
            {data &&
              (data.isArchived ? (
                <Badge variant="neutral" size="sm">
                  Gearchiveerd
                </Badge>
              ) : (
                <Badge variant="success" size="sm">
                  Actief
                </Badge>
              ))}
          </div>
        )}

        {data && (
          <div className="flex items-center gap-2">
            <RequirePermission permission={UPDATE_PERMISSION}>
              <Link
                to="/configurator/vehicle-conversions/$vehicleConversionId/edit"
                params={{ vehicleConversionId }}
                search={{ tab: "algemeen" }}
                className={buttonVariants({ size: "sm" })}
              >
                <Pencil className="size-4" aria-hidden="true" />
                Bewerken
              </Link>
            </RequirePermission>
            <RequirePermission permission={COPY_PERMISSION}>
              <Button
                variant="outline"
                size="sm"
                disabled={copyMutation.isPending}
                onClick={() =>
                  copyMutation.mutate(vehicleConversionId, {
                    onSuccess: (created) => {
                      if (created.id)
                        void navigate({
                          to: "/configurator/vehicle-conversions/$vehicleConversionId",
                          params: { vehicleConversionId: created.id },
                          search: { tab: "algemeen" },
                        });
                    },
                  })
                }
              >
                <Copy className="size-4" aria-hidden="true" />
                Dupliceren
              </Button>
            </RequirePermission>
            <RequirePermission permission={DELETE_PERMISSION}>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setConfirmDelete(true)}
              >
                <Trash2 className="size-4" aria-hidden="true" />
                Verwijderen
              </Button>
            </RequirePermission>
          </div>
        )}
      </div>

      {isError ? (
        <Card className="flex flex-col items-center gap-3 py-10 text-center">
          <p className="text-muted-foreground">
            We konden deze voertuigconversie niet laden.
          </p>
          <Button variant="outline" size="sm" onClick={() => void refetch()}>
            Opnieuw proberen
          </Button>
        </Card>
      ) : isPending ? (
        <Skeleton className="h-96 w-full" />
      ) : (
        <Tabs
          value={tab}
          onValueChange={(value) => {
            if (isDetailTab(value))
              void routeNavigate({
                search: (prev) => ({ ...prev, tab: value }),
              });
          }}
          className="flex flex-col gap-5"
        >
          <TabsList className="self-start">
            <TabsTrigger value="algemeen">Algemeen</TabsTrigger>
            <TabsTrigger value="media">Media</TabsTrigger>
            <TabsTrigger value="inrichting">Inrichting</TabsTrigger>
            <TabsTrigger value="accessoires">
              Inrichting accessoires
            </TabsTrigger>
            <TabsTrigger value="belettering">Belettering</TabsTrigger>
            <TabsTrigger value="telematica">Telematica</TabsTrigger>
            <TabsTrigger value="aanvullend">
              Aanvullende werkzaamheden
            </TabsTrigger>
            <TabsTrigger value="uitsluitingen">Uitsluitingen</TabsTrigger>
          </TabsList>

          <TabsContent value="algemeen">
            <Card className="space-y-4 p-6">
              <dl className="grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2">
                <DetailRow label="Voertuigtype" value={vehicleTypeLabel} />
                <DetailRow label="Titel" value={data.title} />
                <DetailRow label="Code" value={data.code} />
                <DetailRow label="Kleur" value={data.color} />
                <DetailRow label="Bekleding" value={data.upholstery} />
                <DetailRow
                  label="Configuratie-URL"
                  value={data.configurationUrl}
                />
              </dl>
              {data.description && (
                <DetailRow label="Omschrijving" value={data.description} />
              )}
            </Card>
          </TabsContent>

          <TabsContent value="media">
            <Card className="p-6">
              <VehicleConversionMediaTab
                vehicleConversionId={data.id}
                media={data.media}
                canUpdate={false}
              />
            </Card>
          </TabsContent>

          <TabsContent value="inrichting">
            <Card className="p-6">
              <ConversionBuildsTab
                vehicleConversionId={data.id}
                buildGroups={data.buildGroups}
                canUpdate={false}
              />
            </Card>
          </TabsContent>

          <TabsContent value="accessoires">
            <Card className="p-6">
              <BuildAccessoriesTab
                vehicleConversionId={data.id}
                selectedAccessoryIds={data.accessories.flatMap((accessory) =>
                  accessory.id ? [accessory.id] : [],
                )}
                canUpdate={false}
              />
            </Card>
          </TabsContent>

          <TabsContent value="belettering">
            <Card className="p-6">
              <ConversionOptionsTab
                vehicleConversionId={data.id}
                options={data.options}
                optionKind={ConfiguratorOptionKind.lettering}
                canUpdate={false}
              />
            </Card>
          </TabsContent>

          <TabsContent value="telematica">
            <Card className="p-6">
              <ConversionOptionsTab
                vehicleConversionId={data.id}
                options={data.options}
                optionKind={ConfiguratorOptionKind.telematics}
                canUpdate={false}
              />
            </Card>
          </TabsContent>

          <TabsContent value="aanvullend">
            <Card className="p-6">
              <ConversionOptionsTab
                vehicleConversionId={data.id}
                options={data.options}
                optionKind={ConfiguratorOptionKind.other}
                canUpdate={false}
              />
            </Card>
          </TabsContent>

          <TabsContent value="uitsluitingen">
            <Card className="p-6">
              <ConversionExclusionGroupsTab
                vehicleConversionId={data.id}
                options={data.options}
                exclusionGroups={data.exclusionGroups}
                canUpdate={false}
              />
            </Card>
          </TabsContent>
        </Tabs>
      )}

      <Dialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title="Voertuigconversie verwijderen"
        description={`Weet je zeker dat je "${title}" wilt verwijderen?`}
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => setConfirmDelete(false)}
              disabled={deleteMutation.isPending}
            >
              Annuleren
            </Button>
            <Button
              variant="destructive"
              disabled={deleteMutation.isPending}
              onClick={() =>
                deleteMutation.mutate(vehicleConversionId, {
                  onSuccess: () => {
                    setConfirmDelete(false);
                    void navigate({
                      to: "/configurator/vehicle-conversions",
                      search: { page: 1 },
                    });
                  },
                  onError: () =>
                    toast.error("Verwijderen is mislukt. Probeer het opnieuw."),
                })
              }
            >
              <Trash2 className="size-4" aria-hidden="true" />
              Verwijderen
            </Button>
          </>
        }
      />
    </div>
  );
}

function DetailRow({
  label,
  value,
}: {
  readonly label: string;
  readonly value: string | null | undefined;
}): ReactNode {
  return (
    <div>
      <dt className="text-sm text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 text-sm text-foreground">
        {value && value.trim() !== "" ? value : "—"}
      </dd>
    </div>
  );
}
